import { s3ProgressConfig } from "../utils/uploadProgress";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { api } from "./axiosClient";
import type {
  AuditRow,
  Communication,
  EngineSettings,
  EngineSummary,
  EntryComment,
  EntryDetail,
  EntryDocument,
  EntryDraft,
  EntryInbox,
  EntrySearchRequest,
  EntrySearchResponse,
  InvoiceClaim,
  LegalCase,
  Payment,
  PaymentAllocation,
  PlaceholderDefinition,
  PlanActionInput,
  PreviewResponse,
  SettingsResponse,
} from "../features/receivables/receivableEngine";

/** A fresh request id per state-changing call: the server treats a repeat as the same request (no double create or payment). */
const withRequestId = (requestId?: string) => ({ headers: { "x-requestid": requestId ?? uuidv4() } });
const base = "/receivableengine";

type Cmd = { entryId: string; expectedVersion?: number | null; comment?: string | null };

export const receivableEngineApi = {
  // ---------------------------------------------------------------- reading
  search: async (req: EntrySearchRequest): Promise<EntrySearchResponse> => (await api.post(`${base}/search`, req)).data,
  get: async (id: string): Promise<EntryDetail> => (await api.get(`${base}/entry/${id}`)).data,
  communications: async (id: string): Promise<Communication[]> => (await api.get(`${base}/communications/${id}`)).data,
  /** The entry's own inbound address and the mail received at it (newest first). Sent mail stays on communications. */
  inbox: async (id: string): Promise<EntryInbox> => (await api.get(`${base}/inbox/${id}`)).data,
  payments: async (id: string): Promise<Payment[]> => (await api.get(`${base}/payments/${id}`)).data,
  comments: async (id: string): Promise<EntryComment[]> => (await api.get(`${base}/comments/${id}`)).data,
  documents: async (id: string, includeRemoved = false): Promise<EntryDocument[]> =>
    (await api.get(`${base}/documents/${id}`, { params: { includeRemoved } })).data,
  documentLink: async (id: string, documentId: string): Promise<string> => (await api.get(`${base}/documents/${id}/link/${documentId}`)).data.link,
  auditLog: async (id: string): Promise<AuditRow[]> => (await api.get(`${base}/auditlog/${id}`)).data,
  summary: async (req: { departmentIds?: string[]; businessUnitIds?: string[]; from?: string | null; to?: string | null; currency?: number | null; communicationTypes?: number[]; statuses?: number[]; debtorPartyIds?: string[] }): Promise<EngineSummary> =>
    (await api.post(`${base}/summary`, req)).data,
  preview: async (req: { entryId: string; actionId?: string | null; subject?: string | null; body?: string | null }): Promise<PreviewResponse> =>
    (await api.post(`${base}/preview`, req)).data,
  checkInvoices: async (invoiceNumbers: string[], excludeEntryId?: string | null): Promise<InvoiceClaim[]> =>
    (await api.post(`${base}/invoices/check`, { invoiceNumbers, excludeEntryId })).data,
  searchCases: async (searchText: string): Promise<LegalCase[]> => (await api.post(`${base}/cases/search`, { searchText, pageSize: 20 })).data,
  placeholders: async (): Promise<PlaceholderDefinition[]> => (await api.get(`${base}/placeholders`)).data,
  templateText: async (id: string): Promise<{ id: string; name: string; templateType: number; category: number; text: string; placeholders: string[] }> =>
    (await api.get(`${base}/template/${id}/text`)).data,
  starterText: async (actionType: number): Promise<string> => (await api.get(`${base}/starter/${actionType}`)).data.text,
  settings: async (): Promise<SettingsResponse> => (await api.get(`${base}/settings`)).data,
  settingsAudit: async (): Promise<AuditRow[]> => (await api.get(`${base}/settings/audit`)).data,
  saveSettings: async (settings: EngineSettings, expectedVersion: number): Promise<SettingsResponse> =>
    (await api.post(`${base}/settings`, { settings, expectedVersion }, withRequestId())).data,
  /** Runs the due recovery actions and the approval / resume-date reminders now, for the caller's organisation (Org Admin / Super Admin). */
  sendReminders: async (): Promise<{ remindedCount: number; actionsRun: number; remindersSent: number; reassigned: number }> =>
    (await api.post(`${base}/reminders/run`)).data,

  // ---------------------------------------------------------------- drafts
  /** Pass the same requestId on a retry of the same save so it creates one entry. */
  create: async (draft: EntryDraft, requestId?: string): Promise<string> => (await api.post(`${base}/create`, draft, withRequestId(requestId))).data.id,
  update: async (draft: EntryDraft) => api.post(`${base}/update`, draft, withRequestId()),
  submit: async (c: Cmd) => api.post(`${base}/submit`, c, withRequestId()),
  cancel: async (c: Cmd) => api.post(`${base}/cancel`, c, withRequestId()),
  remove: async (c: Cmd) => api.post(`${base}/remove`, c, withRequestId()),

  // ---------------------------------------------------------------- review
  editorReview: async (c: Cmd & { complete: boolean; requestInformation?: boolean }) => api.post(`${base}/editorreview`, c, withRequestId()),
  approve: async (c: Cmd) => api.post(`${base}/approve`, c, withRequestId()),
  reject: async (c: Cmd) => api.post(`${base}/reject`, c, withRequestId()),
  requestChanges: async (c: Cmd) => api.post(`${base}/requestchanges`, c, withRequestId()),

  // ---------------------------------------------------------------- recovery
  pause: async (c: Cmd & { reason: number; reasonText?: string | null; expectedResumeDate?: string | null; documentIds?: string[] | null }) => api.post(`${base}/pause`, c, withRequestId()),
  restart: async (c: Cmd & { mode: number; fromActionId?: string | null; futureActions?: PlanActionInput[] }) => api.post(`${base}/restart`, c, withRequestId()),
  recordPayment: async (
    c: Cmd & {
      paymentDate: string;
      amount: number;
      currency: number;
      paymentReference: string;
      method: number;
      bankReference?: string | null;
      allocations?: PaymentAllocation[];
      documentIds?: string[];
    },
    requestId?: string,
  ): Promise<string> => (await api.post(`${base}/payments/record`, c, withRequestId(requestId))).data.id,
  reversePayment: async (c: Cmd & { paymentId: string }) => api.post(`${base}/payments/reverse`, c, withRequestId()),
  cancelInvoice: async (c: Cmd & { invoiceLineId: string }) => api.post(`${base}/invoices/cancel`, c, withRequestId()),
  completeAction: async (c: Cmd & { actionId: string }) => api.post(`${base}/actions/complete`, c, withRequestId()),
  skipAction: async (c: Cmd & { actionId: string }) => api.post(`${base}/actions/skip`, c, withRequestId()),
  retryCommunication: async (c: Cmd & { actionId: string }) => api.post(`${base}/communications/retry`, c, withRequestId()),
  addComment: async (c: Cmd & { body: string; actionId?: string | null }) => api.post(`${base}/comments/add`, c, withRequestId()),
  removeComment: async (c: Cmd & { itemId: string }) => api.post(`${base}/comments/remove`, c, withRequestId()),

  // ---------------------------------------------------------------- documents
  /** Uploads a file to the entry: pre-signed PUT to storage, then registered with its category. Returns the document id. */
  uploadDocument: async (
    entryId: string,
    file: File,
    category: number,
    extra?: { actionId?: string | null; paymentId?: string | null; invoiceLineId?: string | null },
  ): Promise<string> => {
    const init = (
      await api.post(`${base}/documents/initupload`, { entryId, fileName: file.name, contentType: file.type || "application/octet-stream", sizeBytes: file.size }, withRequestId())
    ).data as { documentId: string; uploadUrl: string };
    await axios.put(init.uploadUrl, file, {
      ...s3ProgressConfig(file), headers: { "Content-Type": file.type || "application/octet-stream" } });
    await api.post(
      `${base}/documents/add`,
      {
        entryId,
        documentId: init.documentId,
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        category,
        actionId: extra?.actionId ?? null,
        paymentId: extra?.paymentId ?? null,
        invoiceLineId: extra?.invoiceLineId ?? null,
      },
      withRequestId(),
    );
    return init.documentId;
  },
  removeDocument: async (c: Cmd & { itemId: string }) => api.post(`${base}/documents/remove`, c, withRequestId()),

  // ---------------------------------------------------------------- disposition
  startDisposition: async (c: Cmd) => api.post(`${base}/startdisposition`, c, withRequestId()),
  reopenRecovery: async (c: Cmd) => api.post(`${base}/reopenrecovery`, c, withRequestId()),
  dispose: async (
    c: Cmd & {
      type: number;
      writeOffAmount?: number | null;
      agreedSettlementAmount?: number | null;
      recoveredSettlementAmount?: number | null;
      settlementDate?: string | null;
      recoveryDate?: string | null;
      reason?: string | null;
      reference?: string | null;
      approvalReference?: string | null;
      caseId?: string | null;
      newCase?: {
        caseNumber: string;
        title?: string;
        caseType: string;
        courtOrForum?: string;
        filingDate?: string | null;
        owner: { id: string; name: string };
        description?: string;
      } | null;
      documentIds?: string[];
    },
  ) => api.post(`${base}/dispose`, c, withRequestId()),
};
